import { createSlice } from "@reduxjs/toolkit";
import Api from "../../api/Api";
import { IObjectKeys } from "../../constants/interfaces";
import { showNotification } from "./admin/notification";

type State = {
	data: { stories: Array<IObjectKeys>, achieved: number, target: number };
	isLoading: boolean;
	isUpdating: boolean;
	error: Object | null;
	timestamp: number;
	isDirty: boolean;
	precalculation: number;
	precalculationTimeseries: any[];
};

const initialState = {
	data: { stories: [], achieved: 0, target: 0 },
	isLoading: true,
	isUpdating: false,
	error: null,
	timestamp: 0,
	isDirty: false,
	precalculation: NaN,
	precalculationTimeseries: [],
};

function shouldUpdateGoal(newData: {[key: string]: any}, oldData: {[key: string]: any}) {
	if (!newData) {
		return false;
	}

	// If id changed or when opening edit goal page
	if (oldData.id === undefined || newData.id !== oldData.id) {
		return true;
	}

	// If achieved metric value has increased
	if (
		newData.achieved !== undefined &&
		oldData.achieved !== undefined &&
		newData.achieved >= oldData.achieved
	) {
		return true;
	}

	// If goal was modified
	if (newData.modification_date && oldData.modification_date && newData.modification_date !== oldData.modification_date) {
		return true;
	}

	// If recurrent goal finished running
	if (newData.recurrencyType !== 'onetime' && newData.iterationStartDate !== oldData.iterationStartDate) {
		return true;
	}

	return false;
}

const goal = createSlice({
	name: "goal",
	initialState,
	reducers: {
		fetchGoalStart: (state: State) => {
			state.isLoading = true;
			state.error = null;
			state.isDirty = false;
		},
		fetchGoalSuccess: (state: State, action) => {
			if (shouldUpdateGoal(action.payload, state.data)) {
				state.data = action.payload;
				
				if (action.payload.metricType === "totalReadingTime") {
					// convert seconds to hours
					state.data.target = action.payload.target / 3600;
					state.data.achieved = action.payload.achieved / 3600;
				}
			}
			
			state.isLoading = false;
			state.error = null;
			state.timestamp = Date.now();
		},
		fetchGoalFailed: (state: State, action) => {
			state.isLoading = false;
			state.error = action.payload;
		},
		fetchPrecalculationStart: (state: State) => {},
		fetchPrecalculationSuccess: (state: State, action) => {
			state.precalculation = action.payload.total || NaN;
			state.precalculationTimeseries = action.payload.graph;
		},
		fetchPrecalculationFailed: (state: State, action) => {
			state.precalculation = NaN;
			state.precalculationTimeseries = [];
		},
	},
});

// Actions
export const {
	fetchGoalStart,
	fetchGoalSuccess,
	fetchGoalFailed,
	fetchPrecalculationStart,
	fetchPrecalculationSuccess,
	fetchPrecalculationFailed,
} = goal.actions;

export default goal.reducer;

// Thunk
export const fetchGoal = (id: any) => async (dispatch: Function) => {
	try {
		dispatch(fetchGoalStart());
		const api = new Api();
		const result = await api.getGoal(id);
		if (result !== "No internet connection") { 
			dispatch(fetchGoalSuccess(result));
		}
		
	} catch (err: any) {
		dispatch(fetchGoalFailed(err));
		dispatch(showNotification("error", err.message));
	}
};

export const fetchPrecalculation =
	(intervalFrom: string, intervalTo: string, metric: string, sections: string[], conversions: string[]) =>
	async (dispatch: Function) => {
		try {
			dispatch(fetchPrecalculationStart());
			const api = new Api();
			const result = await api.getGoalPrecalculation(intervalFrom, intervalTo, metric, sections, conversions);
			if (result !== "No internet connection") { 
				dispatch(fetchPrecalculationSuccess(result));
			}
		} catch (err: any) {
			dispatch(fetchPrecalculationFailed(err));
			dispatch(showNotification("error", err.message));
		}
	};
